import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  AvailabilityClosestWrapper,
  CardHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { useDispatch, useSelector } from 'react-redux';
import { asyncActions } from '@docavenue/core';
import { useRouter } from 'next/dist/client/router';

import { useTranslation } from '@/src/i18n';
import { closestAvailabilitiesActions } from '../../../src/actions';
import { DEFAULT_TIMEZONE, PHYSICAL, TELECONSULTATION } from '@/src/constants';
import { dayjsWithTimezone } from '@/src/utils';
import FirstAvailabilityCalendarSkeleton from '../Availability/FirstAvailabilityCalendarSkeleton';
import AvailabilityCalendarSkeleton from '../Availability/AvailabilityCalendarSkeleton';
import { isNoPractitionerSelected } from '../../utils/card';
import useIsPrescriber from '@/src/hooks/useIsPrescriber';
import CalendarFirstAvailability from '@/components/molecules/FirstAvailability/CalendarFirstAvailability';
import useConsultationReasons from '@/src/hooks/useConsultationReasons';

interface Props {
  card?: CardHcd;
  onClick: (date: any) => void;
  centerId: string | undefined;
  practitionerId: string | undefined;
  consultationReasonName: any;
  consultationType: any;
  closestAvailabilities: {
    closestPhysicalAvailability: any;
    closestTeleconsultationAvailability: any;
    availabilityCount: number;
  } | null;
  specialityId?: string;
}

const getFirstAvailability = (
  data: AvailabilityClosestWrapper,
): dayjs.Dayjs | null => {
  const firstDate = [
    data?.firstAvailabilityStartDateTime,
    data?.firstPhysicalStartDateTime,
    data?.firstTeleconsultationStartDateTime,
    data?.firstHomeVisitStartDateTime,
  ]
    .filter(date => date !== undefined)
    .map(date => dayjs(date));
  return dayjs.min(firstDate);
};

const FirstAvailability = ({
  onClick,
  centerId,
  practitionerId,
  consultationReasonName,
  closestAvailabilities,
  card,
  consultationType,
  specialityId,
}: Props) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const isPrescriber = useIsPrescriber();
  const practitioners = useSelector((state: any) => state.practitioners.items);

  const isPractitionersLoading = useSelector(
    (state: any) => state?.practitioners?.isLoading,
  );
  const isCentersLoading = useSelector(
    (state: any) => state?.centers?.isLoading,
  );
  const timezone = card?.settings?.timeZone || DEFAULT_TIMEZONE;
  const dayjsTZ = dayjsWithTimezone(timezone);
  const [firstAvailability, setFirstAvailability] = React.useState<Dayjs>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [haveNoAvailabilites, setHaveNoAvailabilites] = React.useState<boolean>(
    false,
  );
  const { data: consultationReasons } = useConsultationReasons({
    centerId: centerId ?? card?.center?.id,
    practitionerId: isNoPractitionerSelected(practitionerId)
      ? undefined
      : practitionerId,
    specialityId,
  });
  const noConsultationReasonsAvailable = !(consultationReasons ?? []).find(
    x => x.isAvailable,
  );
  const closestPhysicalAvailability =
    closestAvailabilities?.closestPhysicalAvailability;
  const closestTeleconsultationAvailability =
    closestAvailabilities?.closestTeleconsultationAvailability;
  const closestDate = {
    [PHYSICAL]: () =>
      closestPhysicalAvailability
        ? dayjsTZ(closestPhysicalAvailability.startDateTime)
        : null,
    [TELECONSULTATION]: () =>
      closestTeleconsultationAvailability
        ? dayjsTZ(closestTeleconsultationAvailability.startDateTime)
        : null,
    OTHER: () => {
      if (
        closestAvailabilities?.availabilityCount === 0 ||
        (!closestPhysicalAvailability && !closestTeleconsultationAvailability)
      ) {
        return null;
      }

      if (closestPhysicalAvailability && closestTeleconsultationAvailability) {
        return dayjs.min(
          dayjsTZ(closestPhysicalAvailability.startDateTime),
          dayjsTZ(closestTeleconsultationAvailability.startDateTime),
        );
      }

      return dayjsTZ(
        (closestTeleconsultationAvailability || closestPhysicalAvailability)
          .startDateTime,
      );
    },
  };

  const onClickError = React.useCallback(() => {
    setError(true);
  }, []);

  const onClickAvailability = React.useCallback(() => {
    onClick(
      closestDate[
        ![TELECONSULTATION, PHYSICAL].includes(consultationType)
          ? 'OTHER'
          : consultationType
      ](),
    );
  }, [consultationType, closestDate]);

  React.useEffect(() => {
    if (noConsultationReasonsAvailable) {
      return;
    }
    if (isCentersLoading || (card?.type === 'CENTER' && isPractitionersLoading))
      return;
    let isCancelled = false;
    (async () => {
      setIsLoading(true);
      const from = router.query.focus
        ? dayjs(router.query.focus as string).subtract(2, 'days')
        : dayjs();
      const data = (await asyncActions(
        dispatch,
        closestAvailabilitiesActions.getActions(isPrescriber).getList({
          ...(practitionerId && !isNoPractitionerSelected(practitionerId)
            ? { practitionerId }
            : {}),
          ...(consultationReasonName ? { consultationReasonName } : {}),
          centerId: centerId ?? card?.center?.id,
          from: from
            .add(1, 'minute')
            .startOf('minute')
            .toISOString(),
          specialityId,
        }),
      )) as AvailabilityClosestWrapper;
      if (isCancelled) return;
      const availabilityCount = data.availabilityCount ?? 0;
      if (availabilityCount === 0) {
        setHaveNoAvailabilites(true);
      } else if (availabilityCount > 0) {
        const _firstAvailability = getFirstAvailability(data);
        if (_firstAvailability)
          setFirstAvailability(dayjsTZ(_firstAvailability));
        else setHaveNoAvailabilites(true);
      }
      setIsLoading(false);
    })();
    return () => {
      isCancelled = true;
    };
  }, [
    practitionerId,
    consultationReasonName,
    centerId,
    router,
    card?.center?.id,
    isPractitionersLoading,
    isCentersLoading,
    noConsultationReasonsAvailable,
    specialityId,
  ]);

  React.useEffect(() => {
    if (consultationType) setError(false);
  }, [consultationType]);

  if (isLoading) return <AvailabilityCalendarSkeleton />;

  if (haveNoAvailabilites) {
    return <span>{t('no_availability_for_consultation_reason')}</span>;
  }

  const renderFirstAvailability = () => {
    if (firstAvailability)
      return <CalendarFirstAvailability availability={firstAvailability} />;
    if (noConsultationReasonsAvailable)
      return t('closest_availability_none_short');
    if (consultationReasonName && !practitioners.length)
      return t('no_availability_for_consultation_reason');
    return null;
  };

  return (
    <div className="wrapper-first-availability">
      <div
        className={`first-availability ${
          consultationType && consultationReasonName ? 'is-active' : ''
        }`}
        onClick={
          consultationType && consultationReasonName
            ? onClickAvailability
            : onClickError
        }
      >
        <div className="calendar-info-availability">
          <span
            className="info-availability"
            data-testid="calendar-next-availability"
          >
            {renderFirstAvailability()}
          </span>
        </div>
      </div>
      <FirstAvailabilityCalendarSkeleton timezonePractitioner={timezone} />
      {error && <span>{t('error:all_fields_required')}</span>}
    </div>
  );
};

export default FirstAvailability;
